import { useCallback, useEffect, useState } from 'react';
import { exactTargetReps, gramsToKg, isBodyweightExercise, summariseLoadRange } from '@liftx/core';
import { sessionRepository } from '../../data/repositories/sessionRepository';
import { exerciseRepository } from '../../data/repositories/exerciseRepository';
import { formatExerciseName } from '../../ui/format/exerciseName';
import { ExerciseThumbnail } from '../../ui/components/ExerciseThumbnail';
import { ExerciseInfoDetails } from '../exercises/ExerciseInfoDetails';
import { SetRow } from './SetRow';
import { HoldSetRow } from './HoldSetRow';
import type { PreviousPerformance } from '@liftx/core';
import type { ActiveHoldTimer, ExerciseRecord, SessionExerciseRecord, SessionSetRecord } from '../../data/types';
import styles from './Workout.module.css';

interface PrescriptionSnapshotSet {
  setIndex: number;
  repMin: number | null;
  repMax: number | null;
}

function prescribedSetFor(exercise: SessionExerciseRecord, setIndex: number): PrescriptionSnapshotSet | null {
  const snapshot = exercise.prescriptionSnapshot as { sets?: PrescriptionSnapshotSet[] } | null;
  return snapshot?.sets?.find((s) => s.setIndex === setIndex) ?? null;
}

function targetLabelFor(exercise: SessionExerciseRecord, setIndex: number): string | null {
  const target = prescribedSetFor(exercise, setIndex);
  if (target === null) return null;
  if (target.repMin === null && target.repMax === null) return null;
  if (target.repMin === target.repMax) return `target ${String(target.repMin)} reps`;
  return `target ${String(target.repMin ?? '?')}–${String(target.repMax ?? '?')} reps`;
}

/**
 * The exact planned rep count for this set, when the plan names a single
 * number rather than a genuine range (Planned Reps as Editable Default,
 * Phase 2) — `null` for a genuine range, no prescription at all (e.g. a set
 * added mid-workout, which never gets a `prescriptionSnapshot` entry), or no
 * usable target. Reads the same frozen `prescriptionSnapshot` structured
 * data `targetLabelFor` already does — never parses its rendered text.
 */
function plannedRepsFor(exercise: SessionExerciseRecord, setIndex: number): number | null {
  const target = prescribedSetFor(exercise, setIndex);
  if (target === null) return null;
  return exactTargetReps(target);
}

/** A bodyweight exercise's previous set, as reps only — no weight at all, not
 *  even the em-dash placeholder (Bodyweight Exercises feature, 2026-09-16). */
function formatPreviousBodyweightSet(set: PreviousPerformance['sets'][number]): string {
  return set.reps !== null ? `${String(set.reps)} reps` : '—';
}

/**
 * A Hold exercise's previous set, as duration only — Plank / Hold Exercise
 * Countdown Timer feature. Reps are never meaningful for a Hold exercise
 * (nothing ever records them), so showing `formatPreviousBodyweightSet`'s
 * "reps" wording here would always read as a confusing "— " for every set;
 * this is the "tiny adjustment strictly necessary" the investigation called
 * for, not a previous-performance redesign — weighted-Hold's weight range is
 * still shown separately via the existing `formatPreviousLoadRange` path.
 */
function formatPreviousHoldSet(set: PreviousPerformance['sets'][number]): string {
  return set.durationS !== null ? `${String(set.durationS)}s` : '—';
}

/**
 * The compact "Previous" weight line for a load-bearing exercise — a single
 * value when every qualifying set used the same weight, a low–high range
 * when they differed, or `null` when nothing is loadable at all (Previous
 * Weight Range, Phase 1). `summariseLoadRange` already ignores a `null`
 * load and already returns `null` rather than a degenerate `0–0` range, so
 * this only ever needs to format what it's given, never guard against those
 * cases itself.
 */
function formatPreviousLoadRange(sets: readonly PreviousPerformance['sets'][number][]): string | null {
  const range = summariseLoadRange(sets);
  if (range === null) return null;
  const minKg = gramsToKg(range.minG);
  const maxKg = gramsToKg(range.maxG);
  return minKg === maxKg ? `${String(minKg)} kg` : `${String(minKg)}–${String(maxKg)} kg`;
}

/**
 * One exercise within the workout: expanded shows every set, collapsed
 * shows a one-line summary (`PHASE-0-ARCHITECTURE.md` §14: "current exercise
 * expanded, others collapsed, vertical scroll").
 */
export function ExercisePanel({
  exercise,
  sessionId,
  index,
  expanded,
  onToggleExpand,
  onSetCompleted,
  onRemove,
  activeHoldTimer,
  onStartHoldTimer,
  onClearHoldTimer,
}: {
  exercise: SessionExerciseRecord;
  sessionId: string;
  index: number;
  expanded: boolean;
  onToggleExpand: () => void;
  onSetCompleted: () => void;
  /** Asks the screen to confirm and remove this exercise from the workout. */
  onRemove: () => void;
  /** The session's one active Hold countdown, if any — only consulted when `exercise.modality === 'hold'`; irrelevant and harmlessly ignored for every other exercise. */
  activeHoldTimer: ActiveHoldTimer | null;
  onStartHoldTimer: (sessionSetId: string, durationS: number) => void;
  onClearHoldTimer: () => void;
}): React.JSX.Element {
  const [sets, setSets] = useState<SessionSetRecord[]>([]);
  const [previous, setPrevious] = useState<PreviousPerformance | null>(null);
  const [previousLoading, setPreviousLoading] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [infoExercise, setInfoExercise] = useState<ExerciseRecord | null>(null);
  const [infoLoading, setInfoLoading] = useState(false);
  const [thumbnailExercise, setThumbnailExercise] = useState<ExerciseRecord | null>(null);

  const refresh = useCallback(async () => {
    setSets(await sessionRepository.listSets(exercise.id));
  }, [exercise.id]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (!expanded || exercise.exerciseId === null) return;
    let cancelled = false;
    setPreviousLoading(true);
    void sessionRepository.getPreviousPerformance(exercise.exerciseId, sessionId).then((result) => {
      if (!cancelled) {
        setPrevious(result);
        setPreviousLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [expanded, exercise.exerciseId, sessionId]);

  // Show the same compact, lazy-loaded exercise image used in plans and the
  // library. Session records intentionally only carry an exercise id, so the
  // source record is resolved here without affecting session data or controls.
  useEffect(() => {
    if (exercise.exerciseId === null) {
      setThumbnailExercise(null);
      return;
    }
    let cancelled = false;
    setThumbnailExercise(null);
    void exerciseRepository.get(exercise.exerciseId).then((row) => {
      if (!cancelled) setThumbnailExercise(row ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [exercise.exerciseId]);

  // Fetched only once the info panel is actually opened — the exercise info
  // button is independent of the set-list expand/collapse above, and most
  // panels on screen at once will never be opened, so there's no reason to
  // fetch the full exercise record for every one of them up front.
  useEffect(() => {
    if (!infoOpen || exercise.exerciseId === null) return;
    let cancelled = false;
    setInfoLoading(true);
    void exerciseRepository.get(exercise.exerciseId).then((row) => {
      if (!cancelled) {
        setInfoExercise(row ?? null);
        setInfoLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [infoOpen, exercise.exerciseId]);

  async function handleComplete(
    setId: string,
    // Widened to cover both `SetRow` (reps) and `HoldSetRow` (durationS) —
    // `sessionRepository.completeSet`'s `CompleteSetInput` already accepts
    // either field optionally, so this needed no repository change at all.
    values: { weightG: number | null; reps?: number | null; durationS?: number | null },
  ): Promise<void> {
    await sessionRepository.completeSet(setId, values);
    // A completed set has nothing left to count down for — clear the Hold
    // timer if (and only if) it was the one running on THIS set, so it never
    // lingers and reappears attached to a different set later.
    if (activeHoldTimer?.sessionSetId === setId) {
      onClearHoldTimer();
    }
    await refresh();
    onSetCompleted();
  }

  async function handleUncomplete(setId: string): Promise<void> {
    await sessionRepository.uncompleteSet(setId);
    await refresh();
  }

  async function handleAddSet(): Promise<void> {
    await sessionRepository.addSet(exercise.id);
    await refresh();
  }

  async function handleRemoveSet(setId: string): Promise<void> {
    await sessionRepository.removeSet(setId);
    await refresh();
  }

  const completedCount = sets.filter((s) => s.isCompleted).length;
  const isBodyweight = isBodyweightExercise(exercise);
  const isHold = exercise.modality === 'hold';
  const previousLoadRangeLabel = previous !== null ? formatPreviousLoadRange(previous.sets) : null;

  const exerciseName = formatExerciseName(exercise.exerciseNameSnapshot);

  return (
    <div className={styles.exercisePanel}>
      <div className={styles.exerciseHeader}>
        <button type="button" className={styles.exerciseHeaderMain} onClick={onToggleExpand}>
          {thumbnailExercise !== null && <ExerciseThumbnail exercise={thumbnailExercise} size={112} />}
          <span className={styles.exerciseHeaderText}>
            <span className={styles.exerciseName}>
              {exerciseName}
            </span>
            <span className={styles.exerciseMeta}>
              {completedCount}/{sets.length} sets
            </span>
          </span>
        </button>
        <button
          type="button"
          className={styles.infoButton}
          aria-expanded={infoOpen}
          aria-label={`${infoOpen ? 'Hide' : 'Show'} exercise information for ${exerciseName}`}
          onClick={() => setInfoOpen((value) => !value)}
        >
          {/* Decorative — the button's own `aria-label` already carries the
              accessible name, so the icon itself stays out of the a11y tree. */}
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <circle cx="10" cy="10" r="8.25" stroke="currentColor" strokeWidth="1.5" />
            <circle cx="10" cy="6.5" r="1" fill="currentColor" />
            <path d="M10 9.5V14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      {infoOpen && (
        <div className={styles.infoPanel}>
          {infoLoading && (
            <p role="status" className={styles.stateText}>
              Loading…
            </p>
          )}
          {!infoLoading && infoExercise === null && (
            <p className={styles.stateText}>Exercise information is not available.</p>
          )}
          {!infoLoading && infoExercise !== null && (
            <>
              {/* No `<ExerciseDemonstration>` here, deliberately — the
                  112px thumbnail in the header above already shows the
                  exercise's image, and the demonstration's own GIF load
                  never reliably completed in this panel specifically
                  (tried three targeted fixes; none resolved it). Rather
                  than leave a permanently-stuck "Loading demonstration…"
                  in a panel someone opens mid-set, drop the duplicate. */}
              {infoExercise.description !== null && infoExercise.description.trim().length > 0 && (
                <p className={styles.stateText}>{infoExercise.description}</p>
              )}
              <ExerciseInfoDetails exercise={infoExercise} />
            </>
          )}
        </div>
      )}

      {expanded && (
        <div className={styles.exerciseBody}>
          {/* A bodyweight Hold exercise (e.g. Plank) gets a duration-based
              previous hint, never the reps-based one — reps are never
              meaningful for Hold, and showing it would always read as a
              confusing "— " for every set (Plank / Hold Exercise Countdown
              Timer feature). A weighted Hold still falls through to the
              existing weight-range hint below, unchanged. */}
          {!previousLoading && previous !== null && previous.sets.length > 0 && isBodyweight && isHold && (
            <p className={styles.previousHint}>
              Previous: {previous.sets.map((set) => formatPreviousHoldSet(set)).join(' · ')}
            </p>
          )}
          {!previousLoading && previous !== null && previous.sets.length > 0 && isBodyweight && !isHold && (
            <p className={styles.previousHint}>
              Previous: {previous.sets.map((set) => formatPreviousBodyweightSet(set)).join(' · ')}
            </p>
          )}
          {!previousLoading &&
            previous !== null &&
            previous.sets.length > 0 &&
            !isBodyweight &&
            previousLoadRangeLabel !== null && (
              <p className={styles.previousHint}>Previous: {previousLoadRangeLabel}</p>
            )}
          {!previousLoading && (previous === null || previous.sets.length === 0) && (
            <p className={styles.previousHint}>No previous record for this exercise yet.</p>
          )}

          <ul className={styles.setList}>
            {sets.map((set, setPosition) => {
              const prefill = sessionRepository.prefillFor(previous, set.setIndex);
              const plannedReps = plannedRepsFor(exercise, set.setIndex);
              return isHold ? (
                <HoldSetRow
                  key={set.id}
                  set={set}
                  index={setPosition}
                  hideWeight={isBodyweight}
                  placeholderWeightKg={prefill.loadG === null ? null : gramsToKg(prefill.loadG)}
                  placeholderDurationS={prefill.durationS}
                  canRemove={sets.length > 1}
                  activeTimer={activeHoldTimer?.sessionSetId === set.id ? activeHoldTimer : null}
                  onComplete={(values) => void handleComplete(set.id, values)}
                  onUncomplete={() => void handleUncomplete(set.id)}
                  onRemove={() => void handleRemoveSet(set.id)}
                  onStart={(durationS) => onStartHoldTimer(set.id, durationS)}
                  onStop={onClearHoldTimer}
                />
              ) : (
                <SetRow
                  key={set.id}
                  set={set}
                  index={setPosition}
                  targetLabel={targetLabelFor(exercise, set.setIndex)}
                  hideWeight={isBodyweight}
                  placeholderWeightKg={prefill.loadG === null ? null : gramsToKg(prefill.loadG)}
                  // An exact planned target becomes the input's real value
                  // (below), not a placeholder — showing the previous-reps
                  // placeholder underneath it too would be a conflicting
                  // second signal for the same empty-vs-filled state.
                  placeholderReps={plannedReps === null ? prefill.reps : null}
                  plannedReps={plannedReps}
                  canRemove={sets.length > 1}
                  onComplete={(values) => void handleComplete(set.id, values)}
                  onUncomplete={() => void handleUncomplete(set.id)}
                  onRemove={() => void handleRemoveSet(set.id)}
                />
              );
            })}
          </ul>

          <div className={styles.actions}>
            <button type="button" className={styles.addSetButton} onClick={() => void handleAddSet()}>
              + Add set
            </button>
            <button
              type="button"
              className={styles.addSetButton}
              aria-label={`Remove exercise ${String(index + 1)}`}
              onClick={onRemove}
            >
              Remove exercise
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
